import { CircleAlert, CircleCheck, CircleDashed, CircleX } from "lucide-react";
import type { ReactNode } from "react";
import { routeUrl } from "@/app/routes";
import { ReadCardFrame } from "@/features/copilot/components/read-cards/read-card-frame";
import type {
  SetupStatusData,
  SetupStatusStep,
} from "@/features/copilot/schemas/copilot-read-cards.schema";
import { cn } from "@/shared/lib/cn";

type StepState = SetupStatusStep["state"];

/// The word is the signal; the icon and colour only reinforce it.
const STATE_PRESENTATION: Record<
  StepState,
  { word: string; icon: typeof CircleCheck; tone: string }
> = {
  ready: { word: "Ready", icon: CircleCheck, tone: "text-success-fg" },
  review: { word: "Review", icon: CircleAlert, tone: "text-warning-fg" },
  needs_setup: { word: "Needs setup", icon: CircleX, tone: "text-destructive" },
  optional: { word: "Optional", icon: CircleDashed, tone: "text-text-tertiary" },
};

export interface SetupStatusCardTarget {
  /// The setup page itself, for the "View all" link.
  pageHref: string;
  /// Where one step is fixed, or null when it has no screen of its own.
  stepHref: (stepKey: string) => string | null;
}

/// Where each leave step is fixed. Types are a view of the Plans tab; assigning people happens
/// inside a plan, so those two steps share the plans view.
export function leaveSetupTarget(slug: string): SetupStatusCardTarget {
  const hrefs: Record<string, string> = {
    types: routeUrl.leave.setupTab(slug, "plans", "types"),
    policies: routeUrl.leave.setupTab(slug, "plans", "plans"),
    assignments: routeUrl.leave.setupTab(slug, "plans", "plans"),
    periods: routeUrl.leave.setupTab(slug, "calendar", undefined, "years"),
    blocks: routeUrl.leave.setupTab(slug, "calendar", undefined, "blocked-dates"),
    rollover: routeUrl.leave.setupTab(slug, "calendar", undefined, "rollover"),
  };
  return { pageHref: routeUrl.leave.setup(slug), stepHref: (key) => hrefs[key] ?? null };
}

/// Assigning people happens on a shift, so that step shares the shifts tab.
export function attendanceSetupTarget(slug: string): SetupStatusCardTarget {
  const hrefs: Record<string, string> = {
    calendars: routeUrl.attendance.setupTab(slug, "calendars"),
    shifts: routeUrl.attendance.setupTab(slug, "shifts"),
    assignments: routeUrl.attendance.setupTab(slug, "shifts"),
  };
  return { pageHref: routeUrl.attendance.setup(slug), stepHref: (key) => hrefs[key] ?? null };
}

function StepBody({ step }: { step: SetupStatusStep }): ReactNode {
  const { word, icon: Icon, tone } = STATE_PRESENTATION[step.state];
  return (
    <>
      <Icon aria-hidden="true" className={cn("mt-0.5 size-4 shrink-0", tone)} />
      <span className="min-w-0 flex-1">
        <span className="flex items-baseline justify-between gap-2">
          <span className="truncate font-medium">{step.label}</span>
          <span className={cn("shrink-0 text-xs", tone)}>{word}</span>
        </span>
        <span className="block text-xs text-text-secondary">{step.detail}</span>
      </span>
    </>
  );
}

export function SetupStatusReadCard({
  title,
  steps,
  target,
}: {
  title: string;
  steps: SetupStatusData["steps"];
  target: SetupStatusCardTarget;
}) {
  return (
    <ReadCardFrame title={title} viewAllHref={target.pageHref} hiddenCount={0}>
      {steps.map((step) => {
        // A step that is fine needs no trip to its screen.
        const href =
          step.state === "ready" || step.state === "optional" ? null : target.stepHref(step.key);
        return (
          <li key={step.key}>
            {href ? (
              <a
                href={href}
                className="flex items-start gap-2.5 px-3 py-2 hover:bg-muted/60 focus-visible:outline-2 focus-visible:outline-ring"
              >
                <StepBody step={step} />
              </a>
            ) : (
              <div className="flex items-start gap-2.5 px-3 py-2">
                <StepBody step={step} />
              </div>
            )}
          </li>
        );
      })}
    </ReadCardFrame>
  );
}
